import { useEffect, useState, useRef } from 'react';
import {
  getCarriers,
  updateQuantityLoaded,
  getOrderItemsByCarrierWithEvent,
  getOrderItemsByCarrierId,
  getProductCategories,
  getFurnitureTypesByCategory,
  getFurnitureModelsByTypeId,
  logError
} from '../api';
import { handlePrintSummary } from './Global/DownloadUtils';
import OrderLinksDropdown from './ui/OrderLinksDropdown';
import SpecialProductCarrierRow from './SpecialProductCarrierRow';
import { updateSpecialProductQuantityLoaded } from '../api';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { Form, Button, InputGroup } from 'react-bootstrap';
import Pagination from '../components/Global/Pagination';
import SortableHeader from './ui/SortableHeader';
import { useSorting } from '../hooks/useSorting';
import TotalCount from './Global/TotalCount';
import {
  isTransportistaAndNotOwnerAdminGestion,
  getCarrierId,
  isJefeTransportistas,
  isPropietarioOrAdminOrGestionOrEspectador,
  isPropietarioOrAdminOrGestion
} from '../services/AuthService';

const OrderItemsByCarrierView = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const myCarrierId = isTransportistaAndNotOwnerAdminGestion()
    ? getCarrierId()?.toString()
    : '-1';

  const [filters, setFilters] = useState({
    deliveryDateFrom: format(tomorrow, 'yyyy-MM-dd'),
    deliveryDateTo: format(tomorrow, 'yyyy-MM-dd'),
    carrierId: myCarrierId,
    zipCode: '',
    categoryId: '',
    typeId: '',
    modelId: '',
  });

  const [productCategories, setProductCategories] = useState([]);
  const [furnitureTypes, setFurnitureTypes] = useState([]);
  const [furnitureModels, setFurnitureModels] = useState([]);

  const [carriers, setCarriers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printingResumen, setPrintingResumen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState();
  const [totalCount, setTotalCount] = useState(0);
  const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting('productPrice.type.typeName');

  // groupMode = true: paginado / solo lectura
  // groupMode = false: listado completo + editable
  const [groupMode, setGroupMode] = useState(true);
  const editable = !groupMode;

  // Filtros con debounce para zipCode
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const zipDebounceRef = useRef(null);

  useEffect(() => {
    if (zipDebounceRef.current) clearTimeout(zipDebounceRef.current);
    zipDebounceRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 600);
    return () => clearTimeout(zipDebounceRef.current);
  }, [filters]);

  // Carga inicial de transportistas y categorías
  useEffect(() => {
    if (!isTransportistaAndNotOwnerAdminGestion() || isJefeTransportistas()) {
      getCarriers().then(setCarriers).catch(console.error);
    }
    getProductCategories().then(setProductCategories).catch(logError);
  }, []);

  useEffect(() => {
    if (filters.categoryId) {
      getFurnitureTypesByCategory(filters.categoryId)
        .then(setFurnitureTypes)
        .catch(logError);
      setFurnitureModels([]);
    } else {
      setFurnitureTypes([]);
      setFurnitureModels([]);
    }
  }, [filters.categoryId]);

  useEffect(() => {
    if (filters.typeId) {
      getFurnitureModelsByTypeId(filters.typeId)
        .then(setFurnitureModels)
        .catch(logError);
    } else {
      setFurnitureModels([]);
    }
  }, [filters.typeId]);

  // Fetch items cada vez que cambian filtros, paginación o groupMode
  const prevGroupModeRef = useRef(groupMode);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const prev = prevGroupModeRef.current;
        let result;

        const zipCodes = debouncedFilters.zipCode
          ? debouncedFilters.zipCode.split(',').map(c => c.trim()).filter(Boolean)
          : [];

        if (prev !== groupMode) {
          const typeId = prev && !groupMode ? 1 : !prev && groupMode ? 2 : undefined;
          result = await getOrderItemsByCarrierWithEvent(
            debouncedFilters.carrierId,
            debouncedFilters.deliveryDateFrom,
            debouncedFilters.deliveryDateTo,
            currentPage,
            productsPerPage,
            groupMode,
            typeId,
            orderBy,
            orderDesc,
            zipCodes,
            debouncedFilters.categoryId || null,
            debouncedFilters.typeId || null,
            debouncedFilters.modelId || null
          );
        } else {
          result = await getOrderItemsByCarrierId(
            debouncedFilters.carrierId,
            debouncedFilters.deliveryDateFrom,
            debouncedFilters.deliveryDateTo,
            currentPage,
            productsPerPage,
            groupMode,
            orderBy,
            orderDesc,
            zipCodes,
            debouncedFilters.categoryId || null,
            debouncedFilters.typeId || null,
            debouncedFilters.modelId || null
          );
        }

        setItems(result.items || []);
        setTotalCount(result.totalCount || 0);
        prevGroupModeRef.current = groupMode;
      } catch {
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [debouncedFilters, currentPage, productsPerPage, groupMode, orderBy, orderDesc]);

  // Handlers de filtros
  const handleDateChange = (field, date) => {
    setFilters(prev => ({
      ...prev,
      [field]: date ? format(date, 'yyyy-MM-dd') : '',
    }));
    setCurrentPage(1);
  };
  const handleCarrierChange = e => {
    setFilters(prev => ({ ...prev, carrierId: e.target.value }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      deliveryDateFrom: format(tomorrow, 'yyyy-MM-dd'),
      deliveryDateTo: format(tomorrow, 'yyyy-MM-dd'),
      carrierId: myCarrierId,
      zipCode: '',
      categoryId: '',
      typeId: '',
      modelId: '',
    });
    setCurrentPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'categoryId') { next.typeId = ''; next.modelId = ''; }
      if (field === 'typeId') { next.modelId = ''; }
      return next;
    });
    setCurrentPage(1);
  };
  
  // Ajuste de quantityLoaded (+/-), evita llamadas redundantes
  const adjustQuantity = async (orderItemId, newQty) => {
    const cur = items.find(i => i.id === orderItemId)?.quantityLoaded || 0;
    if (newQty === cur) return;
    try {
      await updateQuantityLoaded(orderItemId, newQty);
      setItems(prev =>
        prev.map(item =>
          item.id === orderItemId ? { ...item, quantityLoaded: newQty } : item
        )
      );
    } catch {
      console.error('Error actualizando quantityLoaded');
    }
  };

  // Variantes de color de fila / tarjeta según estado de carga
  const getRowVariant = item => {
    if (item.quantityLoaded >= item.quantity) return 'table-success';
    if (item.quantityLoaded > 0) return 'table-warning';
    return 'table-danger';
  };
  const getCardVariant = item => {
    if (item.quantityLoaded >= item.quantity) return 'border-success';
    if (item.quantityLoaded > 0) return 'border-warning';
    return 'border-danger';
  };

  // Cálculo de totales y porcentaje
  const totalLoaded = items.reduce((sum, item) => sum + (item.quantityLoaded || 0), 0);
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const loadPercentage =
    totalQuantity > 0 ? Math.round((totalLoaded / totalQuantity) * 100) : 0;
  const anyLoaded = items.some(item => item.quantityLoaded > 0);

  const handleToggleGroupMode = () => {
    setGroupMode(prev => !prev);
    setCurrentPage(1);
  };

  const handlePrintSummaryClick = async () => {
    setPrintingResumen(true);
    try {
      let orderIds;
      
      if (groupMode && totalCount > items.length) {
        try {
          const zipCodes = debouncedFilters.zipCode
            ? debouncedFilters.zipCode.split(',').map(c => c.trim()).filter(Boolean)
            : [];
          const allItems = await getOrderItemsByCarrierId(
            debouncedFilters.carrierId,
            debouncedFilters.deliveryDateFrom,
            debouncedFilters.deliveryDateTo,
            1,
            1000,
            groupMode,
            orderBy,
            orderDesc,
            zipCodes,
            debouncedFilters.categoryId || null,
            debouncedFilters.typeId || null,
            debouncedFilters.modelId || null
          );
          orderIds = [...new Set(allItems.items.flatMap(item => item.orderIds || []))];
        } catch {
          orderIds = [...new Set(items.flatMap(item => item.orderIds || []))];
        }
      } else {
        orderIds = [...new Set(items.flatMap(item => item.orderIds || []))];
      }
      
      await handlePrintSummary(orderIds);
    } finally {
      setPrintingResumen(false);
    }
  };

  return (
    <div className="container my-4">
      <h2 className="mb-4" data-testid="page-title">Artículos a entregar</h2>

      {/* Botones de acción */}
      <div className="mb-3 d-flex justify-content-end gap-2">
        <Button
          variant="outline-danger"
          onClick={handleResetFilters}
          data-testid="reset-filters-btn"
        >
          Reiniciar filtros
        </Button>
        <Button
          variant="outline-info"
          onClick={handlePrintSummaryClick}
          disabled={items.length === 0 || printingResumen}
          data-testid="print-summary-btn"
        >
          {printingResumen ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Imprimiendo...
            </>
          ) : (
            '🖨️ Imprimir Resumen'
          )}
        </Button>
        <Button
          variant={groupMode ? 'primary' : 'secondary'}
          onClick={handleToggleGroupMode}
          data-testid="toggle-group-mode-btn"
        >
          {groupMode
            ? (anyLoaded ? 'Editar carga' : 'Iniciar carga')
            : 'Terminar carga'}
        </Button>
      </div>

      {/* Filtros */}
      <div className="d-flex flex-wrap gap-3 mb-3 align-items-end">
        <Form.Group>
          <Form.Label>Entrega (desde)</Form.Label>
          <div data-testid="delivery-date-from">
            <DatePicker
              selected={filters.deliveryDateFrom ? new Date(filters.deliveryDateFrom) : null}
              onChange={date => handleDateChange('deliveryDateFrom', date)}
              dateFormat="dd/MM/yyyy"
              className="form-control"
            />
          </div>
        </Form.Group>
        <Form.Group>
          <Form.Label>Entrega (hasta)</Form.Label>
          <div data-testid="delivery-date-to">
            <DatePicker
              selected={filters.deliveryDateTo ? new Date(filters.deliveryDateTo) : null}
              onChange={date => handleDateChange('deliveryDateTo', date)}
              dateFormat="dd/MM/yyyy"
              className="form-control"
            />
          </div>
        </Form.Group>
        {(!isTransportistaAndNotOwnerAdminGestion() || isJefeTransportistas()) && (
          <Form.Group>
            <Form.Label>Transportista</Form.Label>
            <Form.Select value={filters.carrierId} onChange={handleCarrierChange} data-testid="carrier-select">
              <option value="-1">Todos</option>
              {isPropietarioOrAdminOrGestionOrEspectador() && (
                <option value="0">Sin asignar</option>
              )}
              {carriers.map(c => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName1}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        <Form.Group>
          <Form.Label>CP (separados por coma)</Form.Label>
          <Form.Control
            type="text"
            value={filters.zipCode}
            onChange={e => handleFilterChange('zipCode', e.target.value)}
            placeholder="28001, 28002..."
            style={{ minWidth: '180px' }}
          />
        </Form.Group>
      </div>

      {/* Filtros de producto */}
      <div className="border rounded p-3 mb-4 bg-light">
        <p className="fw-semibold text-secondary mb-2" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Filtrar por producto
        </p>
        <div className="d-flex flex-wrap gap-3 align-items-end">
          <Form.Group>
            <Form.Label>Categoría</Form.Label>
            <Form.Select value={filters.categoryId} onChange={e => handleFilterChange('categoryId', e.target.value)}>
              <option value="">Todas</option>
              {productCategories.map(c => (
                <option key={c.id} value={c.id}>{c.categoryName}</option>
              ))}
            </Form.Select>
          </Form.Group>
          {furnitureTypes.length > 0 && (
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Form.Select value={filters.typeId} onChange={e => handleFilterChange('typeId', e.target.value)}>
                <option value="">Todos</option>
                {furnitureTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.typeName}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          {furnitureModels.length > 0 && (
            <Form.Group>
              <Form.Label>Modelo</Form.Label>
              <Form.Select value={filters.modelId} onChange={e => handleFilterChange('modelId', e.target.value)}>
                <option value="">Todos</option>
                {furnitureModels.map(m => (
                  <option key={m.id} value={m.id}>{m.modelName}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <span className="spinner-border" role="status" aria-hidden="true" />
        </div>
      ) : (
        <>

      {/* Indicador de progreso */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0" data-testid="progress-text">
            {totalLoaded}/{totalQuantity} cargados ({loadPercentage}%)
          </h5>
          {!loading && <TotalCount totalCount={totalCount} />}
        </div>
        <div className="progress" style={{ height: '1rem' }} data-testid="progress-container">
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${loadPercentage}%` }}
            aria-valuenow={loadPercentage}
            aria-valuemin="0"
            aria-valuemax="100"
            data-testid="progress-bar"
          />
        </div>
      </div>

      {/* Tabla desktop */}
      <div className="table-responsive d-none d-md-block" data-testid="desktop-table">
        <table className="table table-striped">
          <thead>
            <tr>
              <SortableHeader column="productPrice.type.typeName" onSort={handleSort} getSortIcon={getSortIcon}>Tipo</SortableHeader>
              <SortableHeader column="productPrice.model.modelName" onSort={handleSort} getSortIcon={getSortIcon}>Modelo</SortableHeader>
              <SortableHeader column="productPrice.color.colorName" onSort={handleSort} getSortIcon={getSortIcon}>Color</SortableHeader>
              <SortableHeader column="productPrice.size.size" onSort={handleSort} getSortIcon={getSortIcon}>Tamaño</SortableHeader>
              <SortableHeader column="quantity" onSort={handleSort} getSortIcon={getSortIcon}>Cantidad</SortableHeader>
              <th>Variantes</th>
              {isPropietarioOrAdminOrGestion() && groupMode && <th>Pedidos</th>}
              <SortableHeader column="quantityLoaded" onSort={handleSort} getSortIcon={getSortIcon}>Cargado</SortableHeader>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              item.isSpecial ? (
                <SpecialProductCarrierRow 
                  key={`special-${item.id}`}
                  item={item}
                  editable={editable}
                  onUpdate={(id, qty) => {
                    setItems(prev =>
                      prev.map(i =>
                        i.id === id ? { ...i, quantityLoaded: qty } : i
                      )
                    );
                  }}
                />
              ) : (
                <tr className={getRowVariant(item)} key={item.id}>
                  <td>{item.productPrice.type?.typeName}</td>
                  <td>{item.productPrice.model?.modelName}</td>
                  <td>{item.productPrice.color?.colorName || '-'}</td>
                  <td>{item.productPrice.size?.size || '-'}</td>
                  <td>{item.quantity}</td>
                  <td>
                    {item.orderItemVariants && item.orderItemVariants.length > 0 
                      ? item.orderItemVariants.map(v => `${v.variant.name} (+${v.variant.priceDelta}€)`).join(', ')
                      : '-'
                    }
                  </td>
                  {isPropietarioOrAdminOrGestion() && groupMode && (
                    <td>
                      <OrderLinksDropdown 
                        orderIds={item.orderIds} 
                        orderExternalIds={item.orderExternalIds} 
                        isEditing={editable}
                      />
                    </td>
                  )}
                  <td>
                    {editable ? (
                      <InputGroup className="justify-content-center">
                        <Button
                          variant="outline-secondary"
                          size="lg"
                          onClick={() => adjustQuantity(item.id, Math.max(0, (item.quantityLoaded||0) - 1))}
                          data-testid={`decrease-btn-${item.id}`}
                        >
                          -
                        </Button>
                        <InputGroup.Text
                          className="mx-2"
                          style={{ minWidth: '2.5rem', textAlign: 'center' }}
                          data-testid={`quantity-display-${item.id}`}
                        >
                          {item.quantityLoaded || 0}
                        </InputGroup.Text>
                        <Button
                          variant="outline-secondary"
                          size="lg"
                          onClick={() =>
                            adjustQuantity(
                              item.id,
                              Math.min(item.quantity, (item.quantityLoaded||0) + 1)
                            )
                          }
                          data-testid={`increase-btn-${item.id}`}
                        >
                          +
                        </Button>
                      </InputGroup>
                    ) : (
                      <span>{item.quantityLoaded}</span>
                    )}
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards mobile */}
      <div className="d-block d-md-none" data-testid="mobile-cards">
        {items.map(item => (
          <div key={item.id} className={`card mb-2 ${getCardVariant(item)}`}>
            <div className="card-body">
              {item.isSpecial ? (
                <>
                  <p><strong>Tipo:</strong> {item.productPrice?.type?.typeName || '-'}</p>
                  <p>
                    <span className="badge bg-info me-2">Especial</span>
                    <strong>{item.productPrice?.model?.modelName || '-'}</strong>
                  </p>
                  <p><strong>Cantidad:</strong> {item.quantity}</p>
                </>
              ) : (
                <>
                  <p><strong>Tipo:</strong> {item.productPrice?.type?.typeName}</p>
                  <p><strong>Modelo:</strong> {item.productPrice?.model?.modelName}</p>
                  <p><strong>Color:</strong> {item.productPrice?.color?.colorName || '-'}</p>
                  <p><strong>Tamaño:</strong> {item.productPrice?.size?.size || '-'}</p>
                  <p><strong>Cantidad:</strong> {item.quantity}</p>
                  {item.orderItemVariants && item.orderItemVariants.length > 0 && (
                    <p><strong>Variantes:</strong> {item.orderItemVariants.map(v => `${v.variant.name} (+${v.variant.priceDelta}€)`).join(', ')}</p>
                  )}
                  {isPropietarioOrAdminOrGestion() && groupMode && (
                    <p>
                      <strong>Pedidos:</strong>{' '}
                      <OrderLinksDropdown 
                        orderIds={item.orderIds} 
                        orderExternalIds={item.orderExternalIds} 
                        isEditing={editable}
                      />
                    </p>
                  )}
                </>
              )}
              <p>
                <strong>Cargado:</strong>{' '}
                {editable ? (
                  <InputGroup>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      onClick={() => {
                        if (item.isSpecial) {
                          // Para productos especiales, usar el componente especializado
                          const newQty = Math.max(0, (item.quantityLoaded||0) - 1);
                          updateSpecialProductQuantityLoaded(item.id, newQty).then(() => {
                            setItems(prev =>
                              prev.map(i =>
                                i.id === item.id ? { ...i, quantityLoaded: newQty } : i
                              )
                            );
                          });
                        } else {
                          // Para productos normales, usar la función existente
                          adjustQuantity(item.id, Math.max(0, (item.quantityLoaded||0) - 1));
                        }
                      }}
                      data-testid={`mobile-decrease-btn-${item.id}`}
                    >
                      -
                    </Button>
                    <InputGroup.Text
                      className="mx-2"
                      style={{ minWidth: '2.5rem', textAlign: 'center' }}
                      data-testid={`mobile-quantity-display-${item.id}`}
                    >
                      {item.quantityLoaded || 0}
                    </InputGroup.Text>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      onClick={() => {
                        if (item.isSpecial) {
                          // Para productos especiales, usar el componente especializado
                          const newQty = Math.min(item.quantity, (item.quantityLoaded||0) + 1);
                          updateSpecialProductQuantityLoaded(item.id, newQty).then(() => {
                            setItems(prev =>
                              prev.map(i =>
                                i.id === item.id ? { ...i, quantityLoaded: newQty } : i
                              )
                            );
                          });
                        } else {
                          // Para productos normales, usar la función existente
                          adjustQuantity(item.id, Math.min(item.quantity, (item.quantityLoaded||0) + 1));
                        }
                      }}
                      data-testid={`mobile-increase-btn-${item.id}`}
                    >
                      +
                    </Button>
                  </InputGroup>
                ) : (
                  <span>{item.quantityLoaded || 0}</span>
                )}
              </p>
            </div>
          </div>
        ))}
      </div>

      {!items.length && (
        <div className="text-center text-muted mt-3" data-testid="no-items-message">
          No hay artículos para mostrar con los filtros actuales.
        </div>
      )}

      {/* Paginación sólo en groupMode */}
      {groupMode && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination
            totalCount={totalCount}
            currentPage={currentPage}
            itemsPerPage={productsPerPage}
            onPageChange={page => setCurrentPage(page)}
            onItemsPerPageChange={value => {
              setProductsPerPage(value);
              setCurrentPage(1);
            }}

          />
        </div>
      )}

        </>
      )}
    </div>
  );
};

export default OrderItemsByCarrierView;
